import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchArticles } from "../../api/articles";
import { fetchComments, addComment } from "../../api/comments";
import { useAuth } from "../../contexts/AuthContext";
import ShareButton from "../ShareButton/ShareButton";
import { buildNewsArticleShareMessage } from "../../lib/whatsAppShare";

function formatDate(isoString) {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString("de-CH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function emailToDisplayName(email) {
  if (!email || typeof email !== "string") return "User";
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

export default function ArticleDetailView() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchArticles();
        const found = data.find((a) => a.slug === slug);
        setArticle(found || null);
      } catch (err) {
        console.error("Error loading article:", err);
        setError(err.message || "Fehler beim Laden des Artikels.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (!article?.id) return;
    const loadComments = async () => {
      try {
        setCommentsLoading(true);
        const data = await fetchComments(article.id);
        setComments(data);
      } catch (err) {
        console.error("Error loading comments:", err);
      } finally {
        setCommentsLoading(false);
      }
    };
    loadComments();
  }, [article?.id]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!article?.id || !commentBody.trim()) return;
    try {
      setSubmitError(null);
      setSubmitLoading(true);
      await addComment(article.id, commentBody.trim());
      setCommentBody("");
      const data = await fetchComments(article.id);
      setComments(data);
    } catch (err) {
      setSubmitError(err.message || "Fehler beim Speichern.");
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="news-view">
        <header className="news-header">
          <Link to="/news" className="news-back-btn" aria-label="Zurück">
            ← Zurück
          </Link>
        </header>
        <div className="news-loading">Laden…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-view">
        <header className="news-header">
          <Link to="/news" className="news-back-btn" aria-label="Zurück">
            ← Zurück
          </Link>
        </header>
        <div className="news-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="news-view">
        <header className="news-header">
          <Link to="/news" className="news-back-btn" aria-label="Zurück">
            ← Zurück
          </Link>
        </header>
        <div className="news-error">Artikel nicht gefunden.</div>
        <Link to="/news" className="news-back-link">
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const articleShareUrl = `${window.location.origin}/news/${article.slug}`;
  const articleWhatsAppMessage = buildNewsArticleShareMessage({
    title: article.title,
    url: articleShareUrl,
  });

  return (
    <article className="news-view news-article">
      <header className="news-header">
        <Link to="/news" className="news-back-btn" aria-label="Zurück">
          ← Zurück
        </Link>
        <h1 className="news-article-title">{article.title}</h1>
        {article.published_at && (
          <time
            className="news-article-date"
            dateTime={article.published_at}
          >
            {formatDate(article.published_at)}
          </time>
        )}
        <div className="button-row">
          <ShareButton
            whatsappMessage={articleWhatsAppMessage}
            scenario="news"
            shareUrl={articleShareUrl}
          />
        </div>
      </header>

      <div
        className="news-article-body"
        style={{ whiteSpace: "pre-wrap" }}
      >
        {article.body}
      </div>

      <section className="news-comments" aria-label="Kommentare">
        <h2 className="news-comments-title">Kommentare</h2>

        {commentsLoading ? (
          <div className="news-comments-loading">Laden…</div>
        ) : comments.length === 0 ? (
          <p className="news-comments-empty">Noch keine Kommentare.</p>
        ) : (
          <ul className="news-comments-list">
            {comments.map((c) => (
              <li key={c.id} className="news-comment">
                <div className="news-comment-meta">
                  <span className="news-comment-author">{emailToDisplayName(c.author_email)}</span>
                  {c.created_at && (
                    <time
                      className="news-comment-date"
                      dateTime={c.created_at}
                    >
                      {formatDate(c.created_at)}
                    </time>
                  )}
                </div>
                <div className="news-comment-body">{c.body}</div>
              </li>
            ))}
          </ul>
        )}

        {user ? (
          <form
            className="news-comment-form"
            onSubmit={handleSubmitComment}
          >
            <textarea
              className="news-comment-input"
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder="Schreibe einen Kommentar…"
              rows={4}
              maxLength={2000}
              disabled={submitLoading}
              aria-label="Kommentar"
            />
            {submitError && (
              <p className="news-comment-error" role="alert">
                {submitError}
              </p>
            )}
            <button
              type="submit"
              className="news-comment-submit"
              disabled={submitLoading || !commentBody.trim()}
            >
              {submitLoading ? "Wird gesendet…" : "Kommentar absenden"}
            </button>
          </form>
        ) : (
          <p className="news-comments-login">
            <Link to={`/login?redirect=${encodeURIComponent(`/news/${slug}`)}`}>
              Melde dich an, um einen Kommentar zu hinterlassen
            </Link>
          </p>
        )}
      </section>
    </article>
  );
}
